import { FC, useEffect, useRef, useState, useCallback, useMemo } from "react";
import Quill from "quill";

import LineBlotCustom from "../CustomBlot/LineBlotCustom";
import VideoBlotCustom from "../CustomBlot/VideoBlotCustom";
import FileBlotCustom from "../CustomBlot/FileBlotCustom";
import MentionBlotCustom from "../CustomBlot/MentionBlotCustom";
import ImageBlotCustom from "../CustomBlot/ImageBlotCustom";
import ContainerWithOverlay from "../../../UiLib/ContainerWithOverlay/ContainerWithOverlay";

// Регистрация кастомных форматов вынесена в отдельный модуль
const registerCustomBlots = () => {
  Quill.register(MentionBlotCustom);
  Quill.register(VideoBlotCustom);
  Quill.register(LineBlotCustom);
  Quill.register("formats/fileUpload", FileBlotCustom);
  Quill.register("formats/imageCustom", ImageBlotCustom);
};

registerCustomBlots();

export interface PQuillPreview {
  htmlContent: string;
  plainText?: boolean;
}

const QuillPreview: FC<PQuillPreview> = ({ htmlContent, plainText = false }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const quillInstanceRef = useRef<Quill | null>(null);
  const [imgSrc, setImgSrc] = useState<string | undefined>();
  const [imageZoom, setImageZoom] = useState<number>(1);
  const fullscreenImageRef = useRef<HTMLImageElement>(null);

  // Мемоизированная функция для извлечения чистого текста
  const getPlainText = useCallback((html: string): string => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const processNode = (node: ChildNode): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || "";
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tagName = el.tagName.toLowerCase();

        switch (tagName) {
          case "br":
            return "\n";
          case "p":
          case "div":
            return Array.from(el.childNodes).map(processNode).join("") + "\n\n";
          case "ul":
          case "ol":
            return Array.from(el.children)
              .map((child) => "• " + processNode(child))
              .join("\n") + "\n\n";
          case "li":
            return Array.from(el.childNodes).map(processNode).join("");
          default:
            return Array.from(el.childNodes).map(processNode).join("");
        }
      }

      return "";
    };

    return Array.from(tempDiv.childNodes)
      .map(processNode)
      .join("")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }, []);

  // Мемоизированный plain text
  const plainTextContent = useMemo(() => 
    plainText ? getPlainText(htmlContent) : "", 
    [htmlContent, plainText, getPlainText]
  );

  // Обработчик клика по изображению
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const img = target.closest(".image-custom img");
      
      if (img && editorRef.current?.contains(img)) {
        setImgSrc((img as HTMLImageElement).src);
        setImageZoom(1);
      }
    };

    const container = editorRef.current;
    if (!container || plainText) return;

    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, [plainText]);

  // Инициализация Quill
  useEffect(() => {
    if (plainText || !editorRef.current) return;

    // Очистка предыдущего экземпляра
    if (quillInstanceRef.current) {
      const container = editorRef.current;
      if (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      quillInstanceRef.current = null;
    }

    // Создание нового экземпляра Quill
    quillInstanceRef.current = new Quill(editorRef.current, {
      readOnly: true,
      modules: { toolbar: false },
      theme: "snow",
    });

    const delta = quillInstanceRef.current.clipboard.convert({ html: htmlContent });
    quillInstanceRef.current.setContents(delta);

    return () => {
      if (quillInstanceRef.current && editorRef.current?.firstChild) {
        editorRef.current.removeChild(editorRef.current.firstChild);
        quillInstanceRef.current = null;
      }
    };
  }, [htmlContent, plainText]);

  // Обработчик масштабирования изображения
  const handleImageZoom = useCallback((e: WheelEvent) => {
    if (e.ctrlKey) {
      e.preventDefault();
      e.stopPropagation();

      setImageZoom(prevZoom => {
        const newZoom = e.deltaY < 0 ? prevZoom + 0.1 : prevZoom - 0.1;
        return Math.max(0.1, Math.min(5, newZoom)); // Добавлены ограничения
      });
    }
  }, []);

  // Подписка на события колесика мыши
  useEffect(() => {
    if (!imgSrc) return;

    const element = document.querySelector(".overlay_container");
    if (!element) return;

    const handler = (e: WheelEvent) => handleImageZoom(e);
    
    element.addEventListener("wheel", handler as unknown as EventListener, { 
      passive: false 
    });

    return () => {
      element.removeEventListener("wheel", handler as unknown as EventListener);
    };
  }, [imgSrc, handleImageZoom]);

  // Расчет стилей для позиционирования изображения
  const imageContainerStyle = useMemo(() => {
    if (!fullscreenImageRef.current) return {};

    const { clientHeight, clientWidth } = fullscreenImageRef.current;
    return {
      position: "absolute" as const,
      top: `clamp(0px, calc(50% - ${(clientHeight / 2) * imageZoom}px), 100%)`,
      left: `clamp(0px, calc(50% - ${(clientWidth / 2) * imageZoom}px), 100%)`,
    };
  }, [imageZoom]);

  return (
    <div>
      {imgSrc && (
        <ContainerWithOverlay
          style={{ overflow: "scroll" }}
          childrenStyle={imageContainerStyle}
          onOverlayClick={() => setImgSrc(undefined)}
        >
          <img
            alt="attachment"
            ref={fullscreenImageRef}
            src={imgSrc}
            className="fullscreen_image"
            style={{ 
              transform: `scale(${imageZoom})`,
              transformOrigin: "center center"
            }}
          />
        </ContainerWithOverlay>
      )}

      {plainText ? (
        <div className="quill-preview-container plain-text">
          {plainTextContent}
        </div>
      ) : (
        <div ref={editorRef} className="quill-preview-container" />
      )}
    </div>
  );
};

export default QuillPreview;